import { HttpClient } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core'; 
import { Subscription } from "rxjs";

import Raven from 'raven-js';
import { ThemeService } from "libs-frontend-micro-base";

import { ApiGatewayHelperService }from 'modules/app/services/apigateway-helper.service';
import { LoaderService } from 'modules/app/services/loader.service';

import { NetwordDetectionService } from 'modules/app/services/network-detection.service';
import { DownloadHelperService } from 'modules/desktop/services/download-helper.service';
import { LearnerDashboardService } from 'modules/desktop/services/learner-dashboard.service';
import { UserdataService } from 'modules/desktop/services/userdata.service';
import { UmbrellaService } from 'modules/desktop/services/umbrella.service';
import { ClassEnrollmentService } from 'modules/desktop/services/class-enrollments.service';
import { ProductEntitlementsService } from 'modules/desktop/services/product-entitlements.service';
import { DesktopGlobalStateService } from 'modules/desktop/services/desktop-global-state.service';
import { EbookHelperService } from 'modules/desktop/services/ebook-helper.service';
import { AppHelperService } from 'modules/app/services/app-helper.service';
import { DesktopAppEventsService } from 'modules/app/services/desktop-app-events-service';
import { ServerSyncService } from 'modules/desktop/services/server-sync.service';

import config from './config';

@Injectable()
export class DesktopAuthService {
    isUserAuthenticationChecked = false;
    themeService;
    isOnline = false;
    userData: any;

    logoutTriggered = new EventEmitter<string>();

    networkStatusSubscription: Subscription;


    constructor(
        private apiGatewayHelperService: ApiGatewayHelperService,
        private loaderService: LoaderService,
        private networkService: NetwordDetectionService,
        private userdataService: UserdataService,
        private desktopGlobalStateService: DesktopGlobalStateService,
        private desktopAppEventsService: DesktopAppEventsService,
        private downloadHelperService: DownloadHelperService,
        private learnerDashboardService: LearnerDashboardService,
        private umbrellaService: UmbrellaService,
        private classEnrollmentService: ClassEnrollmentService,
        private productEntitlementsService: ProductEntitlementsService,
        private ebookHelperService: EbookHelperService,
        private appHelperService: AppHelperService,
        private http: HttpClient,
        private serverSyncService: ServerSyncService,
    ) {
        this.themeService = new ThemeService();
        
        this.networkService.isOnline(true).then(result => {
            this.isOnline = result;
        });
        this.networkStatusSubscription = this.networkService.monitorStatus().subscribe((isOnline)=>{
          this.isOnline = isOnline;
        })
    }
    
    async logOut(authErrorLogout = false, delay = 0) {
        // console.log("in desktop auth logout");

        if(delay){
            // putting delay provision for NEMO-10155
            // otherwise logout was being called before the services were instantiated
            // as services are being instantiated with a delay of 500ms
            await this.intoduceDelay(delay);
        }

        this.userData = this.userdataService.getLoggedInUser();
    
        this.loaderService.showLoader();
        if (this.downloadHelperService && this.downloadHelperService.downloadingList.size) {
            this.downloadHelperService.resetAvailableDiskSpace();
            await this.downloadHelperService.pauseAllDownloading();
        }
        
        await DLS_IPCCallers.USER_LOGOUT(this.userData);

        this.generateDesktopLogoutEvent();
        
        if(this.isOnline){
            Raven.setUserContext();
            this.loaderService.showOverlayLoader();
            // DO NOT send logout call to mobile-gateway if the logout occured because of 401 auth-error
            !authErrorLogout && await this.http.post(config.app.api.baseUrl + this.apiGatewayHelperService.getApiGatewayPrefixUrl() + 'logout', {}).toPromise();
        } 
        if (this.userData && this.userData.role == 'student') {
            this.learnerDashboardService.resetDashboardDataCache();
        }

        this.umbrellaService.resetLocalCache(true);
        this.classEnrollmentService.resetLocalCache(true);
        this.productEntitlementsService.resetLocalCache(true);
        this.ebookHelperService.resetLocalCache();
        this.desktopGlobalStateService.resetState();
        this.downloadHelperService.resetPPlusBookCodesArray();
        this.downloadHelperService.resetEbookDownloadState();
        this.downloadHelperService.updateAutoUpdateObjectOnNavigation();
        this.downloadHelperService.bookIDtoSpaceIDmap = new Map();
        this.downloadHelperService.resetPPlusBundleMaps();
        this.downloadHelperService.updateSystemDownloads();

        this.desktopAppEventsService.resetSessionInfo();

        this.userdataService.setLoggedInUser();
        this.loaderService.hideLoader();
        
        this.appHelperService.navigateToLoginPage(this.isOnline);
    }

    /**
     * method to introduce delay
     * @param delay : time in ms
     * @returns 
     */
    public intoduceDelay(delay){
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve(true);
            }, delay)
        })
    }
    
    /**
     * checks if the session of last loggedin user's session is still active
     * @param userData 
     * @returns : Boolean
     */
    isUserSessionActive(userData){

        try{
            let sessionExp = userData && userData.metadata && userData.metadata.sessionExpiry;
            if (sessionExp &&
                (+sessionExp > (Date.now() + this.serverSyncService.getServerTimeOffset()))
            ) {
                return true;
            }
        }catch(err){
            // error in calculating session expiry
            return false;
        }

        // session not active if sessionExpiry is not there
        return false;
    }

    /**
     * method to validate whether the session expiry value received from the server is valid
     * i.e. the session expiry is within the maximum allowed expiry
     * @param sessionExp : session expiry timestamp (ms)
     */
    validateSessionExpiryVal(sessionExp){
        let maxLife = config.app.userlogin.maxSessionLife;
        return (+sessionExp <= (Date.now() + this.serverSyncService.getServerTimeOffset() + maxLife));
    }

    /* isUserAuthenticated() {
        const userData = sessionStorage.getItem(appSetting.sessionStorage.userAccount);
        if(userData) {
            this.userAuthenticatedDataSource = 'sessionStorage';
            return Observable.of({
                userAuthenticated: true,
                user: JSON.parse(userData)
            });
        } else {
            return this.http.get(this.apiGatewayHelperService.getApiGatewayPrefixUrl() + 'isUserAuthenticated');
        }
    } */
    

    /**
     * method for user login
     */
    async login(loginId, password){
        let creds = await DLS_IPCCallers.USER_GEN_LOGIN_REQUEST_TOKEN({ loginId, password });
        return await this.http.post(config.app.api.baseUrl + this.apiGatewayHelperService.getApiGatewayPrefixUrl() + 'login', creds).toPromise();
    }


    generateDesktopLogoutEvent() {
        if (this.userData && this.userData.uid) {
            // desktop app events for session start online mode.
            let category = this.desktopAppEventsService.CONSTANTS.EVENT_CATEGORIES.SESSION;
            let action = this.desktopAppEventsService.CONSTANTS.EVENT_ACTIONS.COMPLETE;

            // current page is set in resolver of dashboard and product details. These are the only pages from where logout can be called.
            let navigatedFrom = this.desktopAppEventsService.getCurrentPage();
            let navigatedTo = this.desktopAppEventsService.CONSTANTS.NAVIGATED_PAGE_NAMES.LOGIN;

            if (!this.isOnline) {
                navigatedTo = this.desktopAppEventsService.CONSTANTS.NAVIGATED_PAGE_NAMES.OFFLINE_NO_CONNECTION;
            }

            let eventObject = this.desktopAppEventsService.generateSessionEvent(
                this.userData.uid, this.userData.role, navigatedFrom, navigatedTo, action, category
            );

            this.desktopAppEventsService.generateDesktopEvent(eventObject);
            this.desktopAppEventsService.resetUserData();
        }
    }

}